import { FrequencyAcronymMapping, FrequencyEnum } from "app/services/api/types/medications/enums/FrequencyEnum";
import { MedicationModel } from "app/services/api/types/medications/models/MedicationModel";
import { MedicationTimeOfDayFriendlyNameMapping } from "app/services/api/types/medications/enums/MedicationTimeOfDayEnum";
import { useMemo } from "react";
import { Text } from "react-native";
import { View } from "ui/atoms/view";
import { formatDate } from "ui/utils/dateUtils";
import { getFullName } from "ui/utils/nameUtil";

const DAY_OF_WEEK_LABELS: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const ordinalSuffix = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const WEEKLY_FREQUENCIES: FrequencyEnum[] = [FrequencyEnum.OW, FrequencyEnum.BW];
const MONTHLY_FREQUENCIES: FrequencyEnum[] = [FrequencyEnum.OM, FrequencyEnum.BM];

interface Props {
  medication: MedicationModel;
  className?: string;
  showSignedBy?: boolean;
}
export function MedicationItemSummary({
  medication,
  className,
  showSignedBy = true,
}: Props) {
  const items = useMemo(() => {
    const isWeekly = WEEKLY_FREQUENCIES.includes(medication.frequency);
    const isMonthly = MONTHLY_FREQUENCIES.includes(medication.frequency);

    const daysOfWeekLabel =
      isWeekly && medication.day_of_week?.length
        ? medication.day_of_week
            .map((d) => DAY_OF_WEEK_LABELS[d] ?? d)
            .join(", ")
        : null;

    const dayOfMonthLabel =
      isMonthly && medication.day_of_month
        ? ordinalSuffix(medication.day_of_month)
        : null;

    const timeOfDayLabel =
      (isWeekly || isMonthly) && medication.time_of_day
        ? MedicationTimeOfDayFriendlyNameMapping[medication.time_of_day]
        : null;

    return [
      medication.dosage,
      FrequencyAcronymMapping[medication.frequency] ?? null,
      daysOfWeekLabel,
      dayOfMonthLabel,
      timeOfDayLabel,
      `Start: ${formatDate(medication.start_date)}`,
      medication.end_date ? `End: ${formatDate(medication.end_date)}` : null,
      medication.signed_by && showSignedBy
        ? `Signed off by: ${getFullName(medication.signed_by)}`
        : null,
    ].filter((item) => item != null);
  }, [medication]);

  const isLastItem = (index: number, items: string[]) =>
    index === items.length - 1;

  return (
    <View className={`flex-row flex-wrap ${className}`}>
      {items.map((item, index) => {
        const isLast = isLastItem(index, items);
        return (
          <View key={index} className="flex-row items-center mb-1">
            <Text className="text-typography-600 text-wrap ">{item}</Text>
            <View
              className={`bg-background-300 w-1.5 h-1.5 rounded-full mx-2 ${isLast ? "hidden" : "visible"}`}
            />
          </View>
        );
      })}
    </View>
  );
}
